import React, { useState, useRef, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Label, Input } from "reactstrap";
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { getCroppedImg } from "../../helpers/canvasUtils";

interface CropModalProps {
  isOpen: boolean;
  toggle: () => void;
  imageSrc: string | null;
  onCropSave: (croppedFile: File) => void;
  targetWidth: number;
  targetHeight: number;
  title?: string;
}

const CropModal: React.FC<CropModalProps> = ({
  isOpen,
  toggle,
  imageSrc,
  onCropSave,
  targetWidth,
  targetHeight,
  title = "Adjust Image Crop"
}) => {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement>(null);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget;
    
    // Create a crop that is 90% of the natural image size
    const minDim = Math.min(width, height);
    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: 'px',
          width: minDim * 0.9,
        },
        targetWidth / targetHeight,
        width,
        height
      ),
      width,
      height
    );
    setCrop(initialCrop);
  };

  const handleSave = async () => {
    try {
      if (imageSrc && completedCrop) {
        const croppedImage = await getCroppedImg(imageSrc, completedCrop, targetWidth, targetHeight);
        if (croppedImage) {
          onCropSave(croppedImage);
          toggle();
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>
        {title} (Target: {targetWidth}x{targetHeight} px)
      </ModalHeader>
      <ModalBody className="text-center p-4">
        <div className="mb-3 d-flex justify-content-center bg-light p-2 rounded border" style={{ maxHeight: '500px', overflow: 'auto' }}>
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(c, percentCrop) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={undefined}
              style={{ maxWidth: '100%' }}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imageSrc}
                onLoad={onImageLoad}
                style={{ maxHeight: '450px' }}
              />
            </ReactCrop>
          )}
        </div>

        <div className="d-flex justify-content-center gap-4 mb-3">
          <div className="d-flex align-items-center gap-2">
            <Label className="mb-0 small fw-bold">Width:</Label>
            <Input
              type="number"
              className="form-control-sm text-center"
              style={{ width: '100px' }}
              value={Math.round(crop?.width || 0)}
              onChange={(e) => {
                const val = Number(e.target.value);
                const newCrop = { ...crop!, width: val };
                setCrop(newCrop);
                setCompletedCrop({ ...newCrop, unit: 'px', x: newCrop.x || 0, y: newCrop.y || 0 } as PixelCrop);
              }}
            />
            <span className="small text-muted">px</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Label className="mb-0 small fw-bold">Height:</Label>
            <Input
              type="number"
              className="form-control-sm text-center"
              style={{ width: '100px' }}
              value={Math.round(crop?.height || 0)}
              onChange={(e) => {
                const val = Number(e.target.value);
                const newCrop = { ...crop!, height: val };
                setCrop(newCrop);
                setCompletedCrop({ ...newCrop, unit: 'px', x: newCrop.x || 0, y: newCrop.y || 0 } as PixelCrop);
              }}
            />
            <span className="small text-muted">px</span>
          </div>
        </div>
        <p className="text-info small mb-0">Adjust corners or type exact dimensions. The final output is always {targetWidth}x{targetHeight} px.</p>
      </ModalBody>
      <ModalFooter className="bg-light">
        <div className="hstack gap-2 justify-content-end w-100">
          <Button color="light" onClick={toggle}>Cancel</Button>
          <Button
            style={{ backgroundColor: "#28a8df", borderColor: "#28a8df" }}
            className="text-white px-4"
            onClick={handleSave}
            disabled={!completedCrop}
          >
            Save & Apply Crop
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default CropModal;
